import React from 'react';
import {loadingLottie} from '../../../assets/lottie';
import Lottie from 'lottie-react';
import './styles.css';
import styles from './Button.module.css';
import Tooltip from '../../Tooltip';
import {Icon} from '../../Icon/Icon';

type ButtonUIType =
  | 'action'
  | 'secondary'
  | 'text'
  | 'box'
  | 'action_box'
  | 'text_danger'
  | 'action_danger'
  | 'auth_action'
  | 'hollow'
  | 'hollow_box'
  | 'tab'
  | 'tab_active'
  | 'tapybl_generic'
  | 'signup';

type NativeButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'title' | 'onClick' | 'className' | 'disabled' | 'type' | 'children'
>;

interface Props extends NativeButtonProps {
  /** The button's text. */
  title?: string;
  /** Content after the title, e.g. a count or a custom icon. */
  children?: React.ReactNode;
  onClick?: (e?: any) => void;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  uiType?: ButtonUIType;
  /**
   * An icon-font class (e.g. "icon-plus"), or an SVG file in
   * `src/assets/images` (e.g. "studio-plus.svg") drawn with <Icon>.
   */
  icon?: string;
  /** Where an SVG icon sits; icon-font icons always lead. */
  iconPosition?: 'start' | 'end';
  infoText?: string;
  fullWidth?: boolean;
  tooltip?: string;
  /** Leave unset to keep the browser default (submit inside a form). */
  type?: 'button' | 'submit' | 'reset';
  /** The accessible name; needed when the button shows only an icon. */
  ariaLabel?: string;
  size?: 'default' | 'small';
}

const isSvg = (icon?: string) => !!icon && /\.svg$/i.test(icon);

const Button = React.memo(
  ({
    title,
    children,
    onClick,
    className = '',
    disabled,
    isLoading,
    uiType,
    icon,
    iconPosition = 'start',
    infoText,
    fullWidth,
    tooltip,
    type,
    ariaLabel,
    size = 'default',
    ...rest
  }: Props) => {
    const getRootClass = () => {
      switch (uiType) {
        case 'action':
          return 'ActionButton';
        case 'box':
          return 'BoxButton';
        case 'action_box':
          return 'ActionBoxButton';
        case 'text':
          return 'TextButton';
        case 'text_danger':
          return 'TextDangerButton';
        case 'secondary':
          return 'ActionButton';
        case 'auth_action':
          return 'AuthActionButton';
        case 'signup':
          return 'SignupButton';
        case 'hollow':
          return 'ButtonHollow';
        case 'hollow_box':
          return 'ButtonBoxHollow';
        case 'action_danger':
          return 'DangerButton';
        case 'tab':
          return 'TabButton';
        case 'tab_active':
          return 'TabButtonActive';
        case 'tapybl_generic':
          return 'TapyblGenericButton';
        default:
          return 'ActionButton';
      }
    };

    const svgIcon = isSvg(icon);
    const iconOnly = svgIcon && title === undefined && children === undefined;
    const extraClasses = [
      svgIcon || children !== undefined ? styles.withContent : '',
      size === 'small' ? styles.small : '',
      iconOnly ? styles.iconOnly : '',
    ]
      .filter(Boolean)
      .join(' ');

    const renderSvgIcon = () => (
      <span className={styles.svgIcon} aria-hidden="true">
        <Icon name={icon!} />
      </span>
    );

    const renderLabel = () => (
      <span>
        {icon && !svgIcon && <span className={`${icon} Icon`}> </span>}
        {svgIcon && iconPosition === 'start' && renderSvgIcon()}
        {title !== undefined && <span>{title}</span>}
        {children}
        {svgIcon && iconPosition === 'end' && renderSvgIcon()}
      </span>
    );

    const renderContent = () => (
      <div className="ButtonStyle">
        <button
          {...rest}
          type={type}
          aria-label={ariaLabel ?? rest['aria-label']}
          aria-busy={isLoading || undefined}
          onClick={onClick}
          style={{width: fullWidth ? '100%' : ''}}
          className={`${className} Button ${getRootClass()}${
            extraClasses ? ` ${extraClasses}` : ''
          }`}
          disabled={disabled || isLoading}>
          {isLoading ? (
            <div className="LoadingContianer">
              <Lottie
                animationData={loadingLottie}
                loop={true}
                style={{width: '14px', height: '14px'}}
              />
            </div>
          ) : (
            renderLabel()
          )}
        </button>
        {infoText && <p className="ButtonInfoText">{infoText}</p>}
      </div>
    );

    return tooltip ? (
      <Tooltip tooltipText={tooltip}>{renderContent()}</Tooltip>
    ) : (
      renderContent()
    );
  },
);

export default Button;
